import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { FilteredNodes } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { getInitiativeDates } from '../../utils/getInitiativeDates';

// Constants matching GanttTimelineSection measurements
const GANTT_PADDING_ABOVE = 6;
const GANTT_PARENT_HEIGHT = 28;
const GANTT_CHILD_HEIGHT = 20;
const PARENT_BAR_HEIGHT = 16;
const CHILD_BAR_HEIGHT = 10;

interface GanttParentChildGradientsProps {
  expandedRows: Set<number>;
  initiatives: { cumulativeY: number; depth: number; node: FilteredNodes }[];
  parentChildMap: Map<number, components['schemas']['Initiative'][]>;
  timelineEnd: dayjs.Dayjs;
  timelineStart: dayjs.Dayjs;
}

interface GradientInfo {
  height: number; // Height (pixels)
  parentId: number;
  width: number; // Width (percentage)
  x: number; // Horizontal position (percentage)
  y: number; // Vertical position (pixels)
}

const GanttParentChildGradients: React.FC<GanttParentChildGradientsProps> = ({
  initiatives,
  timelineStart,
  timelineEnd,
  expandedRows,
  parentChildMap,
}) => {
  const gradients = useMemo(() => {
    const gradientsToRender: GradientInfo[] = [];

    // Process each initiative to find expanded parents
    initiatives.forEach((item, index) => {
      const { node, depth, cumulativeY } = item;
      const initiativeId = node.initiatives?.id;

      // Check if this is a parent (depth 0) that is expanded and has children
      if (
        depth === 0 &&
        initiativeId &&
        expandedRows.has(initiativeId) &&
        parentChildMap.has(initiativeId)
      ) {
        // Count visible children in the initiatives array
        let childCount = 0;
        let lastChildCumulativeY = cumulativeY;

        // Look ahead to find children (they should be right after the parent)
        for (let i = index + 1; i < initiatives.length; i += 1) {
          const nextItem = initiatives[i];
          if (nextItem.depth > 0) {
            // This is a child
            childCount += 1;
            lastChildCumulativeY = nextItem.cumulativeY;
          } else {
            // Hit another parent, stop counting
            break;
          }
        }

        if (childCount === 0) return; // No visible children, skip

        // Get parent's dates and position
        const { startDate, endDate } = getInitiativeDates(node.initiatives);
        if (!startDate || !endDate) return; // Skip if parent has no dates

        // Calculate horizontal position and width (same as parent bar)
        const startDateObj = dayjs(startDate);
        const endDateObj = dayjs(endDate);
        const totalDays = timelineEnd.diff(timelineStart, 'days');
        const daysSinceStart = startDateObj.diff(timelineStart, 'days');
        const durationDays = endDateObj.diff(startDateObj, 'days') + 1;

        const leftPercent = (daysSinceStart / totalDays) * 100;
        const widthPercent = (durationDays / totalDays) * 100;

        // Calculate vertical position (starts at bottom of parent bar)
        const parentBarTop =
          cumulativeY + GANTT_PADDING_ABOVE + (GANTT_PARENT_HEIGHT - PARENT_BAR_HEIGHT) / 2;
        const gradientY = parentBarTop + PARENT_BAR_HEIGHT;

        // Calculate height (extends to cover all children)
        const lastChildBarTop =
          lastChildCumulativeY + GANTT_PADDING_ABOVE + (GANTT_CHILD_HEIGHT - CHILD_BAR_HEIGHT) / 2;
        const lastChildBarBottom = lastChildBarTop + CHILD_BAR_HEIGHT;
        const gradientHeight = lastChildBarBottom - gradientY;

        gradientsToRender.push({
          parentId: initiativeId,
          x: Math.max(0, leftPercent),
          y: gradientY,
          width: Math.max(1, widthPercent),
          height: Math.max(0, gradientHeight),
        });
      }
    });

    return gradientsToRender;
  }, [initiatives, timelineStart, timelineEnd, expandedRows, parentChildMap]);

  if (gradients.length === 0) {
    return null;
  }

  return (
    <GradientsContainer>
      {gradients.map(gradient => (
        <GradientElement
          key={gradient.parentId}
          left={gradient.x}
          top={gradient.y}
          width={gradient.width}
          height={gradient.height}
        />
      ))}
    </GradientsContainer>
  );
};

const GradientsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0; // Behind the bars but above the background
`;

const GradientElement = styled.div<{
  height: number;
  left: number;
  top: number;
  width: number;
}>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: ${({ top }) => top}px;
  width: ${({ width }) => width}%;
  height: ${({ height }) => height}px;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  border-radius: 4px;
  pointer-events: none;
`;

export { GanttParentChildGradients };
